import { useUserStore } from '~/stores/User'
import type { AccountModel, BalanceModel } from '@trading/core'
import { useQueryCache } from '@pinia/colada'
import { useAccountsQuery } from '~/queries/accounts'

type FetchAllAccountsOptions = {
  force?: boolean
}

export const useAccountStore = defineStore('accountStore', () => {
  const userStore = useUserStore()
  const balance = ref<string>('0')
  const margin = ref<string>('0')
  const credit = ref<string>('0')
  const freeMargin = ref<string>('0')
  const user = computed(() => userStore.user)
  const currentAccount = ref<AccountModel | undefined>(undefined)
  const accountsQuery = useAccountsQuery()
  const queryCache = useQueryCache()

  const accounts = computed(() => accountsQuery.data.value ?? [])
  const accountLifecyclePending = computed(() => accountsQuery.status.value === 'pending')

  function getWsBalanceService() {
    return useNuxtApp().$wsBalanceService
  }

  async function fetchAccount(options: FetchAllAccountsOptions = {}) {
    if (options.force) {
      await accountsQuery.refetch()
    } else {
      await accountsQuery.refresh()
    }

    const activeAccountId = currentAccount.value?.id
    const data = accounts.value

    const userId = userStore.user?.id
    if (data.length === 0 || !userId) {
      reset()
      return []
    }
    if (!activeAccountId || !selectAccount(activeAccountId)) selectAccount(data[0]!.id)

    return data
  }

  async function getCurrentAccountData() {
    if (currentAccount.value) return currentAccount.value

    await fetchAccount()
    return currentAccount.value
  }

  function selectAccount(accountId: string): boolean {
    const account = accounts.value.find(({ id }) => id === accountId)
    if (!account) return false

    currentAccount.value = account
    applyAccountUpdate(account)
    return true
  }

  function applyAccountUpdate(payload: BalanceModel) {
    balance.value = payload.balance ?? '0'
    margin.value = payload.locked_balance ?? '0'
    freeMargin.value = payload.available_balance ?? '0'
    credit.value = payload.credit ?? '0'
  }

  async function subscribeWs() {
    return getWsBalanceService().subscribeToBalancesUpdate('update', (data: BalanceModel) => {
      if (!('account_id' in data) || typeof data.account_id !== 'string') return

      const account = accounts.value.find(({ id }) => id === data.account_id)
      if (!account) return

      const updatedAccount = {
        ...account,
        currency: data.currency ?? account.currency,
        balance: data.balance,
        credit: data.credit,
        locked_balance: data.locked_balance,
        available_balance: data.available_balance
      }

      queryCache.setQueryData<AccountModel[]>(
        ['accounts'],
        accounts.value.map((item) => (item.id === updatedAccount.id ? updatedAccount : item))
      )

      if (currentAccount.value?.id === updatedAccount.id) {
        currentAccount.value = updatedAccount
        applyAccountUpdate(updatedAccount)
      }
    })
  }

  function unsubscribe(): void {
    const service = getWsBalanceService()
    if (service) {
      service.unsubscribe()
    }
  }

  function reset() {
    balance.value = '0'
    margin.value = '0'
    freeMargin.value = '0'
    currentAccount.value = undefined
  }

  return {
    balance,
    margin,
    freeMargin,
    credit,
    unsubscribe,
    user,
    applyAccountUpdate,
    subscribeWs,
    fetchAccount,
    getCurrentAccountData,
    accountLifecyclePending,
    currentAccount,
    selectAccount,
    reset
  }
})
